import { useEffect, useMemo, useState } from "react";

import Button from "components/Button";
import ChunkUploadFile from "components/ChunkUploadFile";
import Modal from "components/Modal";
import Select from "components/Select";
import { NEW_CARRIER_CATEGORY } from "constants/base";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import UploadItem from "features/main/Loads/modules/UploadItem";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { UploadedFile } from "models/Component";
import { IFactoringPaymentProfile } from "models/HaulPay";
import { updateCarrierProfileAPI } from "services/api/haulpay";
import FMCSALink from "components/FMCSALink";
import CarrierNoaAttachment from "./CarrierNoaAttachment";

type CarrierInfoModalProps = {
  isOpen: boolean;
  closeModal: VoidFunction;
  carrierInfo: IFactoringPaymentProfile;
};

export default function CarrierInfoModal({
  isOpen,
  closeModal,
  carrierInfo
}: Readonly<CarrierInfoModalProps>) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [factoringCompanyRelationId, setFactoringCompanyRelationId] = useState(
    carrierInfo.factoring_company_relation?.id ?? ""
  );
  const [isSavingFactoringCompany, setIsSavingFactoringCompany] = useState(false);

  const factoringCompanyOptions = useMemo(
    () => [
      { id: 0, label: "None", value: "" },
      ...(carrierInfo.factoring_companies ?? []).map((relation, index) => ({
        id: index + 1,
        label: relation.factoring_company_name,
        value: relation.id
      }))
    ],
    [carrierInfo.factoring_companies]
  );

  useEffect(() => {
    setFactoringCompanyRelationId(carrierInfo.factoring_company_relation?.id ?? "");
  }, [carrierInfo.id, carrierInfo.factoring_company_relation?.id]);

  const saveFactoringCompany = async () => {
    try {
      setIsSavingFactoringCompany(true);
      await updateCarrierProfileAPI({
        factoring_id: carrierInfo.factoring.id,
        carrier_id: carrierInfo.id,
        data: {
          factoring_company_relation_id: factoringCompanyRelationId || null
        }
      });
      showSuccessMessage("Carrier factoring company has been updated.");
      closeModal();
    } catch (err: any) {
      showErrorMessage(err.message);
    } finally {
      setIsSavingFactoringCompany(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => {
        closeModal();
        setUploadedFiles([]);
      }}
      wrapperClass="!pt-0"
      dialogClass="!bg-modalBg !pt-6 !mt-20 !gap-5"
      closeClass="!top-24 !right-6 !bg-transparent !text-text">
      <div className="flex flex-col gap-1 pb-3 border-b border-subtitle">
        <h4 className="text-xl font-semibold desktop:text-2xl">Carrier’s info</h4>
        <FMCSALink
          dot={carrierInfo.payment_profile.company_dot}
          mc={carrierInfo.payment_profile.company_mc}>
          FMCSA Profile
        </FMCSALink>
      </div>

      <ul className="flex flex-col h-full gap-5 pb-5 overflow-auto text-xs border-b border-subtitle desktop:text-sm">
        <li className="flex flex-col gap-1">
          <span>Carrier Name</span>
          <span className="text-base font-bold">{carrierInfo.payment_profile.company_name}</span>
        </li>
        <li className="flex flex-col gap-1">
          <span>MC</span>
          <span className="text-base font-bold">{carrierInfo.payment_profile.company_mc}</span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Owner Name</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_name_first}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Carrier Phone</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_phone_number_cell}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Carrier Email</span>
          <span className="text-base font-bold break-words">
            {carrierInfo.payment_profile.owner_email}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>Address</span>
          <span className="text-base font-bold break-words">
            {carrierInfo.payment_profile.owner_address_street_one}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>City</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_address_city}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>ZIP Code</span>
          <span className="text-base font-bold">
            {" "}
            {carrierInfo.payment_profile.owner_address_zip}
          </span>
        </li>
        <li className="flex flex-col gap-1">
          <span>State</span>
          <span className="text-base font-bold">
            {carrierInfo.payment_profile.owner_address_state}
          </span>
        </li>

        <li className="flex flex-col gap-1">
          <Select
            id="carrier-factoring-company"
            label="Factoring Company"
            options={factoringCompanyOptions}
            value={factoringCompanyOptions.find(
              (option) => option.value === factoringCompanyRelationId
            )}
            onChange={(option) => setFactoringCompanyRelationId(option.value)}
            className="w-full"
            btnClass="!py-3"
          />
        </li>

        <CarrierNoaAttachment
          attachment={carrierInfo.payment_profile.factoring_company?.noa_attachment}
        />

        <ChunkUploadFile
          triggerVariant="button"
          categories={NEW_CARRIER_CATEGORY}
          className="flex-none px-5 w-fit"
          multiple
          visibility={false}
          urlCallback={
            UPLOAD_DOCUMENT_URLS({
              payment_profile_id: carrierInfo?.payment_profile_id
            }).NEW_CARRIER
          }
          onUploadFinished={(files: any) => {
            const newCarrierFiles: UploadedFile[] = files.map((file: any) => ({
              name: file.name,
              size: file.fileSize,
              type: file.fileType,
              fileType:
                file.categories && file.categories.length > 0 ? file.categories.split(",") : [""],
              url: file.preview_url
            }));
            setUploadedFiles([...uploadedFiles, ...newCarrierFiles]);
          }}
        />
        {uploadedFiles.map((file) => (
          <div key={file.name} className="mx-3 border border-primary rounded-base">
            <UploadItem file={file} />
          </div>
        ))}
      </ul>

      <footer className="flex justify-end gap-3 mt-5">
        <Button type="button" variant="outline" rounded onClick={closeModal}>
          Cancel
        </Button>
        <Button
          type="button"
          rounded
          loading={isSavingFactoringCompany}
          onClick={saveFactoringCompany}>
          Save
        </Button>
      </footer>
    </Modal>
  );
}
